/**
 * `/settings/team` — people, waiting invitations and the role matrix. Plan §2 U64,
 * `Screen · Settings, Team and API Keys.dc.html`.
 *
 * The matrix's cells are read from `PERMISSIONS`. {@link MATRIX_ROWS} names rows and holds no
 * roles. Controls are offered by `can()`, the function the API's middleware calls.
 */
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  DEFAULT_INVITE_ROLE,
  PERMISSIONS,
  ROLES,
  ROLE_CHOICE_TIER,
  can,
  isPlanGate,
  planRank,
  type Action,
  type InviteSummary,
  type MemberSummary,
  type Resource,
  type Role,
} from "@packagepage/shared";
import { Button, PlanGate, Surface, SurfaceBody, SurfaceHeader } from "@packagepage/ui";

import { problemOf } from "../api/client.js";
import { api } from "../api/procedures.js";

import "@packagepage/ui/tokens.css";
import "@packagepage/ui/surface.css";
import "@packagepage/ui/button.css";
import "./settings.css";
import "./team.css";

const MEMBERS_KEY = ["members"] as const;
const INVITES_KEY = ["invites"] as const;

export const ROLE_LABELS: Record<Role, string> = {
  owner: "Owner",
  admin: "Admin",
  editor: "Editor",
  analyst: "Analyst",
  billing: "Billing",
};

export const MATRIX_ROWS: readonly {
  readonly label: string;
  readonly resource: Resource;
  readonly action: Action;
}[] = [
  { label: "See products, links and the audit log", resource: "product", action: "read" },
  { label: "Create and edit products and links", resource: "product", action: "update" },
  { label: "Make print files", resource: "artwork", action: "create" },
  { label: "Publish a recall", resource: "recall", action: "publish" },
  { label: "Lift a recall", resource: "recall", action: "retire" },
  { label: "Retire a product", resource: "product", action: "retire" },
  { label: "Add or remove a domain", resource: "domain", action: "create" },
  { label: "Invite people", resource: "invite", action: "create" },
  { label: "Change roles and remove people", resource: "member", action: "update" },
  { label: "Billing and plan", resource: "billing", action: "update" },
  { label: "Hand over ownership", resource: "ownership", action: "update" },
];

function messageOf(error: unknown, fallback: string): string {
  return problemOf(error)?.detail ?? fallback;
}

/** The plan a refused mutation names, when the refusal is a plan gate. */
function gatedPlan(error: unknown): string | null {
  const problem = problemOf(error);
  return problem !== null && isPlanGate(problem) ? problem.plan : null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function day(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** `lastActiveAt` can trail by up to a day — `memberSummarySchema`. */
export function lastActiveLabel(iso: string | null): string {
  return iso === null ? "Signed out" : `Around ${day(iso)}`;
}

export function inviteStateLabel(invite: InviteSummary, now: Date): string {
  const left = Math.ceil((Date.parse(invite.expiresAt) - now.getTime()) / DAY_MS);
  if (left <= 0) return `Sent ${day(invite.sentAt)} · expired`;
  return `Sent ${day(invite.sentAt)} · ${String(left)} ${left === 1 ? "day" : "days"} left`;
}

export function RoleMatrix(): ReactNode {
  return (
    <Surface sectioned>
      <SurfaceHeader
        title="What each role can do"
        meta="Five roles · nothing configurable"
      />
      <SurfaceBody>
        <table className="pp-team__matrix" data-role-matrix="true">
          <thead>
            <tr>
              <th scope="col">
                <span className="pp-team__sr">Capability</span>
              </th>
              {ROLES.map((role) => (
                <th key={role} scope="col">
                  {ROLE_LABELS[role]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MATRIX_ROWS.map((row) => (
              <tr key={row.label}>
                <th scope="row">{row.label}</th>
                {ROLES.map((role) => {
                  const yes =
                    PERMISSIONS[row.resource][row.action]?.includes(role) ?? false;
                  return (
                    <td key={role} data-allowed={yes ? "true" : "false"}>
                      {yes ? "Yes" : <span aria-label="No">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <h3 className="pp-team__why">Why editors can publish a recall</h3>
        <p className="pp-settings__note">
          Because the person who finds out about a contamination at 6pm is rarely the
          account owner. Safety actions are never gated behind a seniority level —
          retiring a product, which is irreversible, is.
        </p>
      </SurfaceBody>
    </Surface>
  );
}

function MemberRow({
  member,
  manage,
  roleChoice,
  handOver,
}: {
  readonly member: MemberSummary;
  readonly manage: boolean;
  readonly roleChoice: boolean;
  /** The caller may transfer ownership, and this member is not already an owner. */
  readonly handOver: boolean;
}): ReactNode {
  const client = useQueryClient();
  const [role, setRole] = useState<Role>(member.role);
  const [confirming, setConfirming] = useState(false);
  const settled = () => client.invalidateQueries({ queryKey: MEMBERS_KEY });
  const change = useMutation({
    mutationFn: () => api.setMemberRole({ userId: member.userId, role }),
    onSettled: settled,
  });
  const remove = useMutation({
    mutationFn: () => api.removeMember({ userId: member.userId }),
    onSettled: settled,
  });
  const transfer = useMutation({
    mutationFn: () => api.transferOwnership({ userId: member.userId }),
    onSettled: () => client.invalidateQueries(),
  });
  const failure = change.error ?? remove.error ?? transfer.error;
  // Mirrors the op: below Growth the outgoing owner becomes an editor.
  const outgoing = ROLE_LABELS[roleChoice ? "admin" : "editor"].toLowerCase();

  return (
    <li className="pp-team__row" data-member={member.email}>
      <div className="pp-team__who">
        <span className="pp-team__name">{member.name ?? member.email}</span>
        <span className="pp-team__email">{member.email}</span>
      </div>
      <span className="pp-team__role">{ROLE_LABELS[member.role]}</span>
      <span className="pp-team__when">{lastActiveLabel(member.lastActiveAt)}</span>
      {manage && (
        <div className="pp-team__actions">
          {roleChoice && (
            <label className="pp-team__control">
              <span className="pp-team__sr">Role for {member.email}</span>
              <select
                value={role}
                data-control="role-change"
                onChange={(event) => setRole(event.target.value as Role)}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </label>
          )}
          {roleChoice && (
            <Button
              variant="secondary"
              data-control="role-change"
              disabled={role === member.role}
              loading={change.isPending}
              onClick={() => change.mutate()}
            >
              Change
            </Button>
          )}
          <Button
            variant="secondary"
            data-control="remove"
            loading={remove.isPending}
            onClick={() => remove.mutate()}
          >
            Remove
          </Button>
          {handOver && !confirming && (
            <Button
              variant="secondary"
              data-control="hand-over"
              onClick={() => setConfirming(true)}
            >
              Make owner
            </Button>
          )}
        </div>
      )}
      {handOver && confirming && (
        <div className="pp-team__confirm" data-control="hand-over-confirm">
          <p className="pp-settings__note">
            {member.email} becomes the owner, and you become an {outgoing}. You cannot
            undo this yourself: only the new owner can make you an owner again.
          </p>
          <Button
            variant="primary"
            loading={transfer.isPending}
            onClick={() => transfer.mutate()}
          >
            Hand over ownership
          </Button>
          <Button variant="secondary" onClick={() => setConfirming(false)}>
            Keep it
          </Button>
        </div>
      )}
      {failure !== null && (
        <p className="pp-settings__error" role="alert">
          {messageOf(failure, "We could not reach the server. Nothing was changed.")}
        </p>
      )}
    </li>
  );
}

function InviteRow({ invite }: { readonly invite: InviteSummary }): ReactNode {
  const client = useQueryClient();
  const settled = () => client.invalidateQueries({ queryKey: INVITES_KEY });
  const resend = useMutation({
    mutationFn: () => api.resendInvite({ id: invite.id }),
    onSettled: settled,
  });
  const revoke = useMutation({
    mutationFn: () => api.revokeInvite({ id: invite.id }),
    onSettled: settled,
  });
  const failure = resend.error ?? revoke.error;

  return (
    <li className="pp-team__row" data-invite={invite.email}>
      <div className="pp-team__who">
        <span className="pp-team__name">{invite.email}</span>
        <span className="pp-team__email">{inviteStateLabel(invite, new Date())}</span>
      </div>
      <span className="pp-team__role">{ROLE_LABELS[invite.role]}</span>
      <span className="pp-team__when">Waiting</span>
      <div className="pp-team__actions">
        <Button
          variant="secondary"
          data-control="resend"
          loading={resend.isPending}
          onClick={() => resend.mutate()}
        >
          Resend
        </Button>
        <Button
          variant="secondary"
          data-control="revoke"
          loading={revoke.isPending}
          onClick={() => revoke.mutate()}
        >
          Revoke
        </Button>
      </div>
      {failure !== null && (
        <p className="pp-settings__error" role="alert">
          {messageOf(failure, "We could not reach the server. Nothing was changed.")}
        </p>
      )}
    </li>
  );
}

function InviteForm({ roleChoice }: { readonly roleChoice: boolean }): ReactNode {
  const client = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>(DEFAULT_INVITE_ROLE);
  const invite = useMutation({
    mutationFn: () => api.createInvite({ email, role }),
    onSuccess: () => {
      setEmail("");
      return client.invalidateQueries({ queryKey: INVITES_KEY });
    },
  });
  const gated = invite.isError ? gatedPlan(invite.error) : null;

  return (
    <form
      className="pp-settings__form"
      onSubmit={(event) => {
        event.preventDefault();
        invite.mutate();
      }}
    >
      <label className="pp-settings__label" htmlFor="pp-team-invite-email">
        Invite someone by email
      </label>
      <input
        id="pp-team-invite-email"
        className="pp-settings__input"
        type="email"
        autoComplete="off"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        required
      />
      {roleChoice && (
        <label className="pp-team__control">
          <span className="pp-team__sr">Role</span>
          <select value={role} onChange={(event) => setRole(event.target.value as Role)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </label>
      )}
      <Button variant="primary" type="submit" loading={invite.isPending}>
        {invite.isPending ? "Sending…" : "Send invitation"}
      </Button>
      {invite.isError &&
        (gated === null ? (
          <p className="pp-settings__error" role="alert">
            {messageOf(invite.error, "We could not reach the server. Nothing was sent.")}
          </p>
        ) : (
          <RoleGate />
        ))}
    </form>
  );
}

/** Below Growth everyone is an editor — the mock's state F. */
export function RoleGate(): ReactNode {
  return (
    <PlanGate
      plan={ROLE_CHOICE_TIER}
      feature="Everyone here is an editor"
      why="Invite as many people as you like — seats are free on every plan. Choosing a different role for each person comes with Growth."
      instead="Everyone you invite can edit products, links and print files and publish a recall. Retiring a product and managing the team stay with owners and admins."
    />
  );
}

export function TeamSettings(): ReactNode {
  const current = useQuery({ queryKey: ["orgs", "current"], queryFn: api.currentOrg });
  const members = useQuery({ queryKey: MEMBERS_KEY, queryFn: api.members });
  const role = current.data?.role;
  const admin = role !== undefined && can({ role }, "create", "invite");
  const invites = useQuery({
    queryKey: INVITES_KEY,
    queryFn: api.invites,
    enabled: admin,
  });
  const roleChoice =
    current.data !== undefined &&
    planRank(current.data.plan) >= planRank(ROLE_CHOICE_TIER);
  const manage = role !== undefined && can({ role }, "update", "member");
  const owner = role !== undefined && can({ role }, "update", "ownership");

  return (
    <section className="pp-settings" data-screen-label="Settings · Team & roles">
      <h1 className="pp-settings__title">Team & roles</h1>
      <p className="pp-settings__note">
        Seats are free on every plan.{" "}
        {roleChoice
          ? "Each person's role decides what they can do."
          : "Below Growth everyone you invite is an editor."}
      </p>
      {members.isPending ? (
        <p className="pp-settings__note">Loading your team…</p>
      ) : members.isError ? (
        <p className="pp-settings__error" role="alert">
          {messageOf(members.error, "We could not load your team. Reload to try again.")}
        </p>
      ) : (
        <Surface sectioned>
          <SurfaceHeader title="People" />
          <SurfaceBody>
            <ul className="pp-team__list">
              {members.data.members.map((member) => (
                <MemberRow
                  key={member.userId}
                  member={member}
                  manage={manage}
                  handOver={owner && member.role !== "owner"}
                  roleChoice={roleChoice}
                />
              ))}
              {(invites.data?.invites ?? []).map((invite) => (
                <InviteRow key={invite.id} invite={invite} />
              ))}
            </ul>
            <p className="pp-settings__note">
              Last active is recorded at most once a day, so it can be a day behind.
            </p>
            {admin && <InviteForm roleChoice={roleChoice} />}
          </SurfaceBody>
        </Surface>
      )}
      {current.data !== undefined && !roleChoice && admin && <RoleGate />}
      <RoleMatrix />
    </section>
  );
}
